import { LanguageService } from "./LanguageService";

/**
 * Catalog of participant-facing strings. The EN dictionary is the source of
 * truth for `StringKey`; the DE dictionary must satisfy the same key set,
 * enforced by the type-level guard at the bottom of this file.
 *
 * Excluded by spec (`docs/superpowers/specs/2026-05-18-i18n-de-en-design.md`):
 * the brand-name splash title `K N I G H T  E R R A N D`, the developer-only
 * debug-mode label, generated `@username` author handles, biome flavor names
 * (Mysterious / Forrest / Snowy), and any telemetry event tag.
 */
const EN = {
  // Launcher
  "launcher.subtitle": "study build v0.3",
  "launcher.participant": "Participant",
  "launcher.variant": "Variant",
  "launcher.variant.a": "A — baseline",
  "launcher.variant.b": "B — UGC",
  "launcher.start": "PLAY  ▶",
  "launcher.hint": "← → variant   ·   click / A / Space / Start   ·   X profile",
  "launcher.market.title": "MARKET",
  "launcher.language": "Language",

  // Player profile (own, editable)
  "profile.title": "MY PROFILE",
  "profile.name": "Name",
  "profile.rating": "Rating",
  "profile.rating.none": "no ratings yet",
  "profile.edit_name": "EDIT NAME",
  "profile.new_avatar": "NEW AVATAR",
  "profile.randomize": "RANDOMIZE",
  "profile.save": "SAVE",
  "profile.cancel": "BACK",
  "profile.kb.title": "ENTER YOUR NAME",
  "profile.hint": "↑ ↓ ← → select · J / K / Space confirm · Esc back",
  "profile.rating.value": "★ {avg} · {count} ratings",

  // Review System — end-of-session level rating
  "rating.title": "RATE THIS LEVEL",
  "rating.by": "by {author}",
  "rating.fun": "Fun",
  "rating.difficulty": "Difficulty",
  "rating.submit": "SUBMIT  ▶",
  "rating.skip": "skip · Esc",
  "rating.hint": "↑ ↓ row · ← → rate · A / Start submit",
  "rating.thanks": "thanks for rating!",

  // Asset Creation — fake C: file picker + simulated camera
  "asset.title": "FILE PICKER",
  "asset.up": ".. (up)",
  "asset.camera.take": "[CAM]  Take Photo (simulated)",
  "asset.btn.camera": "Take Photo",
  "asset.btn.cancel": "Cancel",
  "asset.btn.select": "Select",
  "asset.hint": "↑↓ navigate · A select · X photo · Esc back",
  "asset.uploaded": "uploaded!",
  "profile.asset": "Your asset",
  "profile.asset.none": "no asset uploaded",

  // World Globe Level Gallery (market tab)
  "gallery.tab.trades": "TRADES",
  "gallery.tab.gallery": "WORLD GLOBE",
  "gallery.toTrades": "◀ TRADES",
  "gallery.hint": "↑↓ hop maps  ·  Y/J select  ·  ◀ / Q trades  ·  Esc exit",
  "review.snippet.0": "Great level",
  "review.snippet.1": "Best creator",
  "review.snippet.2": "Tough but fair",
  "review.snippet.3": "Lovely design",
  "review.snippet.4": "Replay this!",
  "review.snippet.5": "A bit short",
  "review.snippet.6": "Clever traps",

  // HUD
  "hud.tutorial": "TUTORIAL",
  "hud.paused": "PAUSED",
  "hud.pause.hint": "press  P  or click  ▷  to resume",
  "hud.pause.tooltip": "PAUSE · P",
  "hud.resume.tooltip": "RESUME · P",
  "hud.bottom.chunk": "chunk",
  "hud.bottom.defeated": "defeated",
  "hud.bottom.sidequests": "sidequests",
  "hud.bottom.depth": "DEPTH",
  "hud.bottom.solved": "SOLVED",
  "hud.bottom.boost": "BOOST",
  "hud.bottom.feather": "U-feather",
  "hud.bottom.abort": "Esc/Start abort",
  "hud.bottom.bg": "bg",

  // Tutorial cue titles
  "tutorial.jump": "JUMP",
  "tutorial.collect": "COLLECT",
  "tutorial.attack": "ATTACK",
  "tutorial.interact": "INTERACT",
  "tutorial.backstep": "BACKSTEP",
  "tutorial.double_jump": "DOUBLE-JUMP",
  "tutorial.climb": "CLIMB",

  // Skill tooltips (HUD icons)
  "tooltip.skill.jump": "JUMP · Space / A",
  "tooltip.skill.double_jump": "DOUBLE-JUMP · tap Space twice",
  "tooltip.skill.double_jump.locked": "DOUBLE-JUMP · locked (collect {geo} geo)",
  "tooltip.skill.attack": "ATTACK · J / X",
  "tooltip.skill.interact": "INTERACT · K / Y (near a gate)",
  "tooltip.boot.active": "SPEED BOOST · {sec}s left",
  "tooltip.boot.inactive": "SPEED BOOST · collect a Boot pickup",

  // Runner banners — sidequest entry/exit
  "banner.sidequest.enter": "SIDEQUEST · ENTER",
  "banner.sidequest.cleared": "SIDEQUEST · CLEARED",
  "banner.sublevel.depth": "SUB-LEVEL · DEPTH {depth}",
  "banner.sublevel.returned": "RETURNED TO DEPTH {depth}",
  "banner.sidequest.biome": "SIDEQUEST · {biome}",

  // Runner banners — puzzles
  "banner.gate_locked.title": "GATE LOCKED",
  "banner.gate_locked.fallback": "complete the puzzle",
  "banner.puzzle.solved.title": "PUZZLE SOLVED",
  "banner.puzzle.solved.subtitle": "GATE UNLOCKED",
  "puzzle.label.kills": "PUZZLE · DEFEAT GEGNER",
  "puzzle.label.crystals": "PUZZLE · COLLECT CRYSTALS",
  "puzzle.label.lever": "PUZZLE · PULL THE LEVER",

  // Runner banners — skill unlock / feather / boost
  "banner.skill.unlocked": "NEW SKILL UNLOCKED",
  "skill.jump": "JUMP",
  "skill.double_jump": "DOUBLE JUMP",
  "skill.attack": "ATTACK",
  "skill.interact": "INTERACT",
  "banner.feather.title": "FEATHER",
  "banner.feather.no_skill": "skill traded away — pick one up to re-learn",
  "banner.feather.no_charges": "no charges — collect a feather first",
  "banner.escape_feather.title": "ESCAPE FEATHER",
  "banner.escape_feather.subtitle": "press U to fly up one level · {stored} stored",
  "banner.speed_boost.title": "SPEED BOOST",
  "banner.speed_boost.subtitle": "FAST FOOTWORK · {sec}s",
  "banner.author.subtitle": "created by @{name}",
  "banner.canopy.title": "TREE CANOPY",
  "banner.canopy.subtitle": "hold ↓ to drop  ·  Y at the far vine to slide down",

  // Map breadcrumb root
  "map.main_label": "Mysterious Forest (main)",
  "banner.main_level": "MYSTERIOUS FOREST",
  "map.title": "~ WORLD MAP ~",
  "map.here": " (here)",
  "map.close_hint": "press  M  /  Esc  to close",
  "map.footer.hp": "HP",
  "map.footer.geo": "geo",
  "map.footer.defeated": "defeated",
  "map.footer.sidequests": "sidequests",

  // Biome / pack flavor labels
  "biome.mysterious": "Mysterious",
  "biome.forrest": "Forrest",
  "biome.snowy": "Snowy",

  // Background presets — flavor names ("City of Rain" etc.) treated as
  // proper-noun place names (like brand-title) and not translated. Only the
  // "Default" fallback is localised.
  "preset.default": "Default",

  // Shop
  "shop.title": "MARKET",
  "shop.locating": "locating…",
  "shop.exit": "EXIT",
  "shop.toGlobe": "WORLD GLOBE ▶",
  "shop.hp": "HP",
  "shop.inv": "INV",
  "shop.skl": "SKL",
  "shop.action.trade": "[Y/J] TRADE",
  "shop.action.insufficient": "INSUFFICIENT",
  "shop.hint.stand_on_platform": "STAND ON A PLATFORM",
  "shop.hint.exit": "walk left  ·  Esc",
  "shop.hint.controls": "↑↓ hop platforms  ·  Y/J trade  ·  → / E globe",
  "shop.scroll.new": "NEW ▲",
  "shop.need.geo": "NEED {n} GEO",
  "shop.need.hp": "NEED {n} HP",
  "shop.need.feather": "NEED {n} FEATHER",
  "shop.need.item": "NEED {item}",
  "shop.missing.skill": "MISSING {skill} SKILL",
  "shop.skill.short.double_jump": "DBL-JUMP",
  "shop.skill.short.feather": "FEATHER",

  // Author profile tooltip (hover/click on procedural-author surfaces)
  "profile.member_since": "Member since {month}.{year}",
  "profile.stats": "{levels} levels · {trades} trades",
  "profile.hint.hud_click": "click for profile",

  // Pause menu (Variant B: a level/creator info column + an action column)
  "pause.col.level": "LEVEL",
  "pause.col.creator": "CREATED BY",
  "pause.button.author": "✎  AUTHOR SIDEQUEST",
  "pause.button.market": "$   MARKET",
  "pause.button.gallery": "◎  LEVEL GALLERY",
  "pause.button.profile": "☺  MY PROFILE",

  // Sidequest Author editor (Variant B)
  "author.title": "AUTHOR A SIDEQUEST",
  "author.field.name": "Name",
  "author.field.author": "Author",
  "author.field.puzzle": "Puzzle",
  "author.field.biome": "Biome",
  "author.field.setpieces": "Set pieces",
  "author.field.setpieces.hint": "Y to toggle",
  "author.puzzle.kills": "defeat 3",
  "author.puzzle.crystals": "collect 3",
  "author.puzzle.lever": "pull lever",
  "author.cost.line": "Cost: 📜 {cost} scroll  ·  you have: {have}",
  "author.button.cancel": "CANCEL",
  "author.button.save": "SAVE & PLACE",
  "author.disabled.no_scroll": "no scrolls — collect one first",
  "author.disabled.no_name": "name can't be empty",
  "author.disabled.no_author": "author handle can't be empty",
  "author.hint.pool": "← →  cycle  ·  type to write your own",
  "author.hint.freetext": "typing  ·  ← →  back to pool",
  "author.discard.title": "Discard this sidequest?",
  "author.discard.confirm": "Y discard  ·  N keep editing",
  "author.banner.placed": "SIDEQUEST PLACED",

  // Set-piece chip labels
  "author.setpiece.bird_trio": "bird trio",
  "author.setpiece.gegner_cluster": "gegner cluster",
  "author.setpiece.lever_maze": "lever maze",
  "author.setpiece.crystal_arc": "crystal arc",
  "author.setpiece.heart_sanctuary": "heart sanctuary",
  "author.setpiece.boot_dash": "boot dash",
  "author.setpiece.feather_cache": "feather cache",
  "author.setpiece.twin_portals": "twin portals",

  // End scene
  "end.header": "──  SESSION  END  ──",
  "end.reason.hp_down": "fell · 0 HP",
  "end.reason.timeup": "time up",
  "end.meta": "Variant: {variant}   ·   Participant: {participant}",
  "end.duration": "Duration: {sec}s  ·  Tutorial excluded",
  "end.flushed": "Telemetry buffer flushed to localStorage",
  "end.return_to_launcher": "Return to Launcher",
  "end.stat.geo": "geo collected",
  "end.stat.gegner": "gegner defeated",
  "end.stat.gegner.suffix": "seen",
  "end.stat.attacks": "attacks landed",
  "end.stat.double_jumps": "double-jumps performed",
  "end.stat.sidequests": "sidequests accepted/cleared",
  "end.stat.inventory": "inventory items collected",
  "end.stat.hcb": "hearts / crystals / boots",
  "end.stat.towers": "towers authored",
  "end.stat.background_uploads": "background uploads applied",
  "end.b_only": "[B-only]",
  "end.ugc.background": "UGC · background \"{label}\"",
} as const;

export type StringKey = keyof typeof EN;

const DE: Record<StringKey, string> = {
  // Launcher
  "launcher.subtitle": "Studienversion v0.3",
  "launcher.participant": "Teilnehmer:in",
  "launcher.variant": "Variante",
  "launcher.variant.a": "A — Standard",
  "launcher.variant.b": "B — UGC",
  "launcher.start": "SPIELEN  ▶",
  "launcher.hint": "← → Variante   ·   Klick / A / Leertaste / Start   ·   X Profil",
  "launcher.market.title": "MARKT",
  "launcher.language": "Sprache",

  // Player profile (own, editable)
  "profile.title": "MEIN PROFIL",
  "profile.name": "Name",
  "profile.rating": "Bewertung",
  "profile.rating.none": "noch keine Bewertungen",
  "profile.edit_name": "NAME ÄNDERN",
  "profile.new_avatar": "NEUER AVATAR",
  "profile.randomize": "ZUFÄLLIG",
  "profile.save": "SPEICHERN",
  "profile.cancel": "ZURÜCK",
  "profile.kb.title": "NAME EINGEBEN",
  "profile.hint": "↑ ↓ ← → wählen · J / K / Leertaste bestätigen · Esc zurück",
  "profile.rating.value": "★ {avg} · {count} Bewertungen",

  // Review System — Level-Bewertung am Sessionende
  "rating.title": "LEVEL BEWERTEN",
  "rating.by": "von {author}",
  "rating.fun": "Spaß",
  "rating.difficulty": "Schwierigkeit",
  "rating.submit": "ABSCHICKEN  ▶",
  "rating.skip": "überspringen · Esc",
  "rating.hint": "↑ ↓ Zeile · ← → bewerten · A / Start senden",
  "rating.thanks": "danke fürs Bewerten!",

  // Asset Creation — falscher C:-Dateiauswähler + simulierte Kamera
  "asset.title": "DATEI-AUSWAHL",
  "asset.up": ".. (zurück)",
  "asset.camera.take": "[CAM]  Foto aufnehmen (simuliert)",
  "asset.btn.camera": "Foto",
  "asset.btn.cancel": "Abbrechen",
  "asset.btn.select": "Auswählen",
  "asset.hint": "↑↓ navigieren · A wählen · X Foto · Esc zurück",
  "asset.uploaded": "hochgeladen!",
  "profile.asset": "Dein Asset",
  "profile.asset.none": "kein Asset hochgeladen",

  // Weltkugel-Level-Galerie (Markt-Tab)
  "gallery.tab.trades": "TAUSCH",
  "gallery.tab.gallery": "WELTKUGEL",
  "gallery.toTrades": "◀ TAUSCH",
  "gallery.hint": "↑↓ Karten wechseln  ·  Y/J wählen  ·  ◀ / Q Tausch  ·  Esc raus",
  "review.snippet.0": "Gutes Level",
  "review.snippet.1": "Bester Creator",
  "review.snippet.2": "Knackig aber fair",
  "review.snippet.3": "Schönes Design",
  "review.snippet.4": "Nochmal spielen!",
  "review.snippet.5": "Etwas kurz",
  "review.snippet.6": "Clevere Fallen",

  // HUD
  "hud.tutorial": "TUTORIAL",
  "hud.paused": "PAUSIERT",
  "hud.pause.hint": "P drücken oder ▷ klicken zum Fortsetzen",
  "hud.pause.tooltip": "PAUSE · P",
  "hud.resume.tooltip": "WEITER · P",
  "hud.bottom.chunk": "Abschnitt",
  "hud.bottom.defeated": "besiegt",
  "hud.bottom.sidequests": "Quests",
  "hud.bottom.depth": "TIEFE",
  "hud.bottom.solved": "GELÖST",
  "hud.bottom.boost": "BOOST",
  "hud.bottom.feather": "U-Feder",
  "hud.bottom.abort": "Esc/Start abbrechen",
  "hud.bottom.bg": "BG",

  // Tutorial cue titles
  "tutorial.jump": "SPRINGEN",
  "tutorial.collect": "SAMMELN",
  "tutorial.attack": "ANGRIFF",
  "tutorial.interact": "AKTION",
  "tutorial.backstep": "RÜCK",
  "tutorial.double_jump": "DOPPEL-SPRUNG",
  "tutorial.climb": "KLETTERN",

  // Skill tooltips
  "tooltip.skill.jump": "SPRINGEN · Leertaste / A",
  "tooltip.skill.double_jump": "DOPPEL-SPRUNG · Leertaste zweimal",
  "tooltip.skill.double_jump.locked": "DOPPEL-SPRUNG · gesperrt (sammle {geo} Geo)",
  "tooltip.skill.attack": "ANGRIFF · J / X",
  "tooltip.skill.interact": "AKTION · K / Y (am Tor)",
  "tooltip.boot.active": "SPEED BOOST · {sec}s übrig",
  "tooltip.boot.inactive": "SPEED BOOST · sammle einen Stiefel",

  // Runner banners — sidequest entry/exit
  "banner.sidequest.enter": "SIDEQUEST · BETRETEN",
  "banner.sidequest.cleared": "SIDEQUEST · ABGESCHLOSSEN",
  "banner.sublevel.depth": "UNTER-LEVEL · TIEFE {depth}",
  "banner.sublevel.returned": "ZURÜCK AUF TIEFE {depth}",
  "banner.sidequest.biome": "SIDEQUEST · {biome}",

  // Runner banners — puzzles
  "banner.gate_locked.title": "TOR VERSCHLOSSEN",
  "banner.gate_locked.fallback": "Rätsel lösen",
  "banner.puzzle.solved.title": "RÄTSEL GELÖST",
  "banner.puzzle.solved.subtitle": "TOR OFFEN",
  "puzzle.label.kills": "RÄTSEL · GEGNER BESIEGEN",
  "puzzle.label.crystals": "RÄTSEL · KRISTALLE SAMMELN",
  "puzzle.label.lever": "RÄTSEL · HEBEL ZIEHEN",

  // Runner banners — skill unlock / feather / boost
  "banner.skill.unlocked": "NEUE FÄHIGKEIT",
  "skill.jump": "SPRINGEN",
  "skill.double_jump": "DOPPEL-SPRUNG",
  "skill.attack": "ANGRIFF",
  "skill.interact": "AKTION",
  "banner.feather.title": "FEDER",
  "banner.feather.no_skill": "Fähigkeit weggegeben — sammle eine neue Feder",
  "banner.feather.no_charges": "keine Ladungen — sammle erst eine Feder",
  "banner.escape_feather.title": "FLUCHT-FEDER",
  "banner.escape_feather.subtitle": "U drücken, um ein Level aufzusteigen · {stored} gespeichert",
  "banner.speed_boost.title": "SPEED BOOST",
  "banner.speed_boost.subtitle": "FLINKE BEINE · {sec}s",
  "banner.author.subtitle": "erstellt von @{name}",
  "banner.canopy.title": "BAUMKRONE",
  "banner.canopy.subtitle": "↓ halten zum Abspringen  ·  Y an der zweiten Liane zum Hinabgleiten",

  // Map breadcrumb root
  "map.main_label": "Geheimnisvoller Wald (Haupt)",
  "banner.main_level": "GEHEIMNISVOLLER WALD",
  "map.title": "~ WELTKARTE ~",
  "map.here": " (hier)",
  "map.close_hint": "M  /  Esc  zum Schließen",
  "map.footer.hp": "HP",
  "map.footer.geo": "Geo",
  "map.footer.defeated": "besiegt",
  "map.footer.sidequests": "Quests",

  // Biome / pack flavor labels
  "biome.mysterious": "Geheimnisvoll",
  "biome.forrest": "Wald",
  "biome.snowy": "Verschneit",

  // Background presets — proper-noun place names (City of Rain etc.) stay
  // English; only the generic "Default" gets localised.
  "preset.default": "Standard",

  // Shop
  "shop.title": "MARKT",
  "shop.locating": "lokalisiere…",
  "shop.exit": "RAUS",
  "shop.toGlobe": "WELTKUGEL ▶",
  "shop.hp": "HP",
  "shop.inv": "INV",
  "shop.skl": "SKL",
  "shop.action.trade": "[Y/J] TAUSCHEN",
  "shop.action.insufficient": "ZU WENIG",
  "shop.hint.stand_on_platform": "AUF EINE PLATTFORM STELLEN",
  "shop.hint.exit": "links gehen  ·  Esc",
  "shop.hint.controls": "↑↓ Plattformen wechseln  ·  Y/J tauschen  ·  → / E Weltkugel",
  "shop.scroll.new": "NEU ▲",
  "shop.need.geo": "{n} GEO FEHLEN",
  "shop.need.hp": "{n} HP FEHLEN",
  "shop.need.feather": "{n} FEDER FEHLEN",
  "shop.need.item": "{item} FEHLT",
  "shop.missing.skill": "{skill} FÄHIGKEIT FEHLT",
  "shop.skill.short.double_jump": "DPL-SPRUNG",
  "shop.skill.short.feather": "FEDER",

  // Author profile tooltip
  "profile.member_since": "Mitglied seit {month}.{year}",
  "profile.stats": "{levels} Level · {trades} Trades",
  "profile.hint.hud_click": "Klicken für Profil",

  // Pause menu
  "pause.col.level": "LEVEL",
  "pause.col.creator": "ERSTELLT VON",
  "pause.button.author": "✎  SIDEQUEST ERSTELLEN",
  "pause.button.market": "$   MARKT",
  "pause.button.gallery": "◎  LEVEL-GALERIE",
  "pause.button.profile": "☺  MEIN PROFIL",

  // Sidequest Author editor
  "author.title": "SIDEQUEST ERSTELLEN",
  "author.field.name": "Name",
  "author.field.author": "Autor",
  "author.field.puzzle": "Rätsel",
  "author.field.biome": "Biom",
  "author.field.setpieces": "Bausteine",
  "author.field.setpieces.hint": "Y umschalten",
  "author.puzzle.kills": "3 besiegen",
  "author.puzzle.crystals": "3 sammeln",
  "author.puzzle.lever": "Hebel ziehen",
  "author.cost.line": "Kosten: 📜 {cost} Rolle  ·  vorhanden: {have}",
  "author.button.cancel": "ABBRECHEN",
  "author.button.save": "SPEICHERN & PLATZIEREN",
  "author.disabled.no_scroll": "keine Rollen — sammle zuerst eine",
  "author.disabled.no_name": "Name darf nicht leer sein",
  "author.disabled.no_author": "Autor-Handle darf nicht leer sein",
  "author.hint.pool": "← →  wechseln  ·  tippe deinen eigenen",
  "author.hint.freetext": "Eingabe  ·  ← →  zurück zur Liste",
  "author.discard.title": "Sidequest verwerfen?",
  "author.discard.confirm": "Y verwerfen  ·  N weiter",
  "author.banner.placed": "SIDEQUEST PLATZIERT",

  // Set-piece chip labels
  "author.setpiece.bird_trio": "Vogelschwarm",
  "author.setpiece.gegner_cluster": "Gegnergruppe",
  "author.setpiece.lever_maze": "Hebellabyrinth",
  "author.setpiece.crystal_arc": "Kristallbogen",
  "author.setpiece.heart_sanctuary": "Herzversteck",
  "author.setpiece.boot_dash": "Stiefelsprint",
  "author.setpiece.feather_cache": "Federlager",
  "author.setpiece.twin_portals": "Doppelportal",

  // End scene
  "end.header": "──  SESSION  ENDE  ──",
  "end.reason.hp_down": "gefallen · 0 HP",
  "end.reason.timeup": "Zeit abgelaufen",
  "end.meta": "Variante: {variant}   ·   Teilnehmer:in: {participant}",
  "end.duration": "Dauer: {sec}s  ·  Tutorial ausgeschlossen",
  "end.flushed": "Telemetrie in localStorage gespeichert",
  "end.return_to_launcher": "Zurück zum Launcher",
  "end.stat.geo": "Geo gesammelt",
  "end.stat.gegner": "Gegner besiegt",
  "end.stat.gegner.suffix": "gesehen",
  "end.stat.attacks": "Angriffe getroffen",
  "end.stat.double_jumps": "Doppel-Sprünge",
  "end.stat.sidequests": "Sidequests angenommen/abgeschlossen",
  "end.stat.inventory": "Inventar-Gegenstände",
  "end.stat.hcb": "Herzen / Kristalle / Stiefel",
  "end.stat.towers": "Türme erstellt",
  "end.stat.background_uploads": "Hintergründe angewendet",
  "end.b_only": "[nur B]",
  "end.ugc.background": "UGC · Hintergrund \"{label}\"",
};

export const STRINGS = { en: EN, de: DE } as const;

/**
 * Look up a string in the active language and substitute `{name}` placeholders
 * from `params`. Missing placeholder values leave the literal `{name}` in
 * place — placeholders shouldn't go missing in practice since callers and the
 * catalog are authored by the same dev.
 */
export function t(key: StringKey, params?: Record<string, string | number>): string {
  const lang = LanguageService.getLanguage();
  const dict = lang === "de" ? DE : EN;
  const raw = dict[key];
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (match, name: string) => {
    const v = params[name];
    return v === undefined ? match : String(v);
  });
}

// Compile-time guard: DE must satisfy the same key set as EN. If a key goes
// missing from DE this assignment fails `pnpm typecheck`.
type _AssertDEMatchesEN = Record<StringKey, string>;
const _de: _AssertDEMatchesEN = DE;
void _de;
